import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { fmtR, fmtMoney, rColor, fmtTradeDate } from '../../lib/tradeUtils';

function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2 text-sm last:border-0">
      <span className="text-zinc-500">{label}</span>
      <span className="font-medium text-white tnum">{value}</span>
    </div>
  );
}

export default function TradeDetailModal({ tradeId, onClose, onEdit, onDeleted }) {
  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!tradeId) { setTrade(null); setConfirm(false); return; }
    setLoading(true);
    api.get(`/trades/${tradeId}`)
      .then(({ data }) => setTrade(data.trade))
      .catch(() => { toast.error('Could not load trade'); onClose?.(); })
      .finally(() => setLoading(false));
  }, [tradeId]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/trades/${tradeId}`);
      toast.success('Trade deleted');
      onDeleted?.();
    } catch {
      toast.error('Could not delete trade');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AnimatePresence>
      {tradeId && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 sm:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="relative z-10 my-4 w-full max-w-md rounded-2xl border border-white/10 bg-ink-card p-6"
            data-testid="trade-detail-modal"
          >
            <button onClick={onClose} className="absolute right-5 top-5 text-zinc-500 hover:text-white" data-testid="trade-detail-close"><X size={20} /></button>

            {loading || !trade ? (
              <div className="grid h-48 place-items-center"><Spinner className="h-6 w-6" /></div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="font-heading text-2xl font-bold text-white">{trade.ticker}</span>
                  <span className={`ml-auto font-heading text-3xl font-bold tnum ${rColor(trade.r_multiple)}`}>
                    {trade.r_multiple === null ? '—' : fmtR(trade.r_multiple, 2)}
                  </span>
                </div>
                <div className="mt-1 text-xs text-zinc-500">{fmtTradeDate(trade.traded_at)}</div>

                <div className="mt-4">
                  <Row label="Total P&L" value={trade.pnl !== null && trade.pnl !== undefined ? fmtMoney(trade.pnl, true) : null} />
                  <Row label="Risk" value={trade.risk_amount !== null && trade.risk_amount !== undefined ? fmtMoney(trade.risk_amount) : null} />
                  <Row label="R Multiple" value={trade.r_multiple !== null ? fmtR(trade.r_multiple, 2) : null} />
                  <Row label="Outcome" value={trade.outcome ? trade.outcome.toUpperCase() : null} />
                </div>

                {trade.notes ? (
                  <div className="mt-4">
                    <div className="mb-1 text-xs text-zinc-500">Notes</div>
                    <p className="whitespace-pre-wrap rounded-xl bg-ink-surface p-3 text-sm text-zinc-300">{trade.notes}</p>
                  </div>
                ) : null}

                {trade.screenshot_url ? (
                  <a href={trade.screenshot_url} target="_blank" rel="noopener noreferrer" className="mt-4 block overflow-hidden rounded-xl border border-white/10">
                    <img src={trade.screenshot_url} alt="Trade screenshot" className="w-full" data-testid="trade-detail-screenshot" />
                  </a>
                ) : null}

                <div className="mt-6 flex gap-3">
                  {confirm ? (
                    <>
                      <Button variant="danger" className="flex-1" onClick={doDelete} disabled={deleting} data-testid="trade-confirm-delete">
                        {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} Confirm delete
                      </Button>
                      <Button variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" className="flex-1" onClick={() => onEdit?.(trade)} data-testid="trade-edit-button"><Pencil size={16} /> Edit</Button>
                      <Button variant="ghost" onClick={() => setConfirm(true)} data-testid="trade-delete-button"><Trash2 size={16} /></Button>
                    </>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
